/* ------------------------------------------------------------------ *
 *  FilterSidebar – dynamic filter drawer for OMOP tables             *
 * ------------------------------------------------------------------ */
import PropTypes from "prop-types";
import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Scrollbar from "./ScrollBar";

/* ---------- helpers ------------------------------------------------- */
const isNumeric = (v) =>
  typeof v === "number" || /^-?\d+$/.test(String(v));

const isDateLike = (v) =>
  !isNumeric(v) && !isNaN(Date.parse(v));

/*  Detect columns that hold full date‑time values  */
const isDateTimeColumn = (col) => col.endsWith("_datetime");
const isNumericColumn = (col, sampleVal) =>
  isNumeric(sampleVal) || col === "value_as_number" || col === "range_low" || col === "range_high";
const isIdColumn = (col) =>
  col === "person_id" ||
  col === "location_id" ||
  col === "provider_id" ||
  col === "care_site_id" ||
  col.endsWith("_id") ||
  col.endsWith("_concept_id");

/* ------------------------------------------------------------------ */
export default function FilterSidebar({
  openFilter,
  onCloseFilter,
  headers,
  sampleRow,
  filters,
  onFilterChange,
  onApply,
  onClear,
}) {
  const [searchTerm, setSearchTerm] = useState("");

  /* active badge count */
  const activeCount = Object.values(filters).filter(
    (v) =>
      v !== "" &&
      v !== null &&
      v !== undefined &&
      !(Array.isArray(v) && v.length === 0)
  ).length;

  /* -------- render a field block --------------------------------- */
  const renderField = (col) => {
    const sampleVal = sampleRow[col];

    /* 1. Raw IDs / Concept IDs → paste box ------------------------ */
    if (isIdColumn(col)) {
      return (
        <TextField
          key={col}
          label={`${col} (comma / space separated)`}
          size="small"
          multiline
          minRows={2}
          placeholder="e.g. 8507, 8532  9001"
          value={filters[col] ?? ""}
          onChange={(e) => onFilterChange(col, e.target.value)}
          fullWidth
        />
      );
    }

    /* 2. Dates & DateTimes → range pickers ----------------------- */
    if (isDateLike(sampleVal) || col.endsWith("_date") || isDateTimeColumn(col)) {
      const inputType = isDateTimeColumn(col) ? "datetime-local" : "date";
      return (
        <Stack key={col} direction="row" spacing={1}>
          <TextField
            label={`${col} ≥`}
            type={inputType}
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters[`${col}_min`] ?? ""}
            onChange={(e) => onFilterChange(`${col}_min`, e.target.value)}
            fullWidth
          />
          <TextField
            label={`${col} ≤`}
            type={inputType}
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters[`${col}_max`] ?? ""}
            onChange={(e) => onFilterChange(`${col}_max`, e.target.value)}
            fullWidth
          />
        </Stack>
      );
    }

    /* 3. Numeric → min/max inputs -------------------------------- */
    if (isNumericColumn(col, sampleVal)) {
      return (
        <Stack key={col} direction="row" spacing={1}>
          <TextField
            label={`${col} ≥`}
            type="number"
            size="small"
            value={filters[`${col}_min`] ?? ""}
            onChange={(e) => onFilterChange(`${col}_min`, e.target.value)}
            fullWidth
          />
          <TextField
            label={`${col} ≤`}
            type="number"
            size="small"
            value={filters[`${col}_max`] ?? ""}
            onChange={(e) => onFilterChange(`${col}_max`, e.target.value)}
            fullWidth
          />
        </Stack>
      );
    }

    /* 4. Text → contains search ---------------------------------- */
    return (
      <TextField
        key={col}
        label={col}
        size="small"
        placeholder="contains…"
        value={filters[col] ?? ""}
        onChange={(e) => onFilterChange(col, e.target.value)}
        fullWidth
      />
    );
  };

  /* headers filtered by search term */
  const shownHeaders = headers.filter((h) =>
    h.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /* ---------------- drawer UI ----------------------------------- */
  return (
    <Drawer
      anchor="right"
      open={openFilter}
      onClose={onCloseFilter}
      PaperProps={{
        sx: { width: 300, border: "none", overflow: "hidden", bgcolor: "#fff" },
      }}
    >
      {/* header */}
      <Stack direction="row" alignItems="center" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          Filters
        </Typography>
        <Chip label={activeCount} size="small" />
      </Stack>

      <Divider />

      {/* search box */}
      <TextField
        fullWidth
        size="small"
        placeholder="Type to search field…"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ m: 2 }}
      />

      {/* field list */}
      <Scrollbar>
        <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
          {shownHeaders.map(renderField)}
        </Stack>
      </Scrollbar>

      {/* footer */}
      <Box sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Button fullWidth variant="contained" onClick={onApply}>
            Apply
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              onClear();
              setSearchTerm("");
            }}
          >
            Clear All
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}

FilterSidebar.propTypes = {
  openFilter:     PropTypes.bool.isRequired,
  onCloseFilter:  PropTypes.func.isRequired,
  headers:        PropTypes.arrayOf(PropTypes.string).isRequired,
  sampleRow:      PropTypes.object.isRequired,
  filters:        PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onApply:        PropTypes.func.isRequired,
  onClear:        PropTypes.func.isRequired,
};
